import { createSelector } from 'reselect'
import { getYearEndModuleStatusesByType } from '@/features/year-end-module-status/baseYearEndModuleStatus.selector'
import {
  YearEndModuleType,
  YearEndModuleStatusOptions,
} from '@/features/year-end-module-status/yearEndModuleStatus.slice'
import {
  selectCatchUpBookkeepingStatusForCurrentUser,
  selectIsCatchUpBookkeepingComplete,
} from '@/components/CatchUpBookkeepingStatus/catchUpBookkeepingStatus.selector'
import { selectGettingStartedUserOnboardingSteps } from '@/features/onboarding/user-onboarding-steps/onboarding.selectors'
import {
  GettingStartedOnboardingStep,
  GettingStartedOnboardingStepId,
} from '@/features/onboarding/user-onboarding-steps/onboarding.reducer'
import { selectCurrentAnnualTaxDetails } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import {
  selectCurrentAnnualTaxFiling,
  selectTskIsScorp,
} from '@/features/taxes/annual-taxes/annualTaxFilings.selector'

/**
 * EXPEDITE YOUR CATCH-UP BOOKKEEPING cards
 * These cards are shown with URGENT badges when the deadline banner is visible
 */
export const EXPEDITE_CARD_IDS: GettingStartedOnboardingStepId[] = [
  GettingStartedOnboardingStep.connectBusinessBankAccount,
  GettingStartedOnboardingStep.uploadBankStatements,
  GettingStartedOnboardingStep.markPersonalTransactions,
  GettingStartedOnboardingStep.clarifyUncategorizedTransactions,
]

/**
 * Card headlines for display in CUBK bubble subtext
 */
export const EXPEDITE_CARD_HEADLINES: Record<
  GettingStartedOnboardingStepId,
  string
> = {
  [GettingStartedOnboardingStep.connectBusinessBankAccount]:
    'Connect business bank account',
  [GettingStartedOnboardingStep.uploadBankStatements]: 'Upload bank statements',
  [GettingStartedOnboardingStep.markPersonalTransactions]:
    'Mark personal transactions',
  [GettingStartedOnboardingStep.clarifyUncategorizedTransactions]:
    'Clarify unknown transactions',
  // Other steps (not shown in bubble but needed for type completeness)
  [GettingStartedOnboardingStep.joinGroupOnboardingSession]:
    'Join a group onboarding session',
  [GettingStartedOnboardingStep.yearEndWrapUpSurvey]: 'Tell us your tax needs',
  [GettingStartedOnboardingStep.complete_1099_survey]: 'Complete 1099 Kickoff',
  [GettingStartedOnboardingStep.payrollInterest]: 'Get started with payroll',
  [GettingStartedOnboardingStep.connectHeardBank]:
    'Complete Heard Banking setup',
}

/**
 * Selects whether user is a late joiner based on current tax filing
 */
export const selectUserIsLateJoinerForCubk = createSelector(
  selectCurrentAnnualTaxFiling,
  (taxFiling) => Boolean(taxFiling?.isLateJoiner)
)

/**
 * Selects whether TSK (Tax Season Kickoff) is complete for current tax year
 * TSK is complete when pretaxSurveySubmittedAt exists
 */
export const selectIsTskComplete = createSelector(
  selectCurrentAnnualTaxFiling,
  (taxFiling) => Boolean(taxFiling?.pretaxSurveySubmittedAt)
)

/**
 * Selects whether Verify Tax Entity module is complete
 */
export const selectIsVerifyTaxEntityComplete = createSelector(
  getYearEndModuleStatusesByType,
  (byModuleType) =>
    byModuleType[YearEndModuleType.verifyTaxEntity]?.status ===
    YearEndModuleStatusOptions.complete
)

/** Open (incomplete) EXPEDITE cards. CLARIFY excluded until followup is requested. */
export const selectOpenExpediteCards = createSelector(
  selectGettingStartedUserOnboardingSteps,
  selectCatchUpBookkeepingStatusForCurrentUser,
  (steps, cubkStatus) => {
    const followupRequested = Boolean(cubkStatus?.followupRequestedAt)
    return steps
      .filter(
        (step) =>
          EXPEDITE_CARD_IDS.includes(step.onboardingStep.identifier) &&
          !step.completedAt &&
          !step.skippedAt
      )
      .filter((step) => {
        if (
          step.onboardingStep.identifier ===
          GettingStartedOnboardingStep.clarifyUncategorizedTransactions
        ) {
          return followupRequested
        }
        return true
      })
      .map((step) => step.onboardingStep.identifier)
  }
)

/**
 * Selects whether any EXPEDITE card is open
 */
export const selectHasOpenExpediteCards = createSelector(
  selectOpenExpediteCards,
  (openCards) => openCards.length > 0
)

/**
 * Selects the top (first) open EXPEDITE card for bubble subtext display
 * Priority order matches EXPEDITE_CARD_IDS array
 */
export const selectTopOpenExpediteCard = createSelector(
  selectOpenExpediteCards,
  (openCards) => {
    // Return first card in priority order
    for (const cardId of EXPEDITE_CARD_IDS) {
      if (openCards.includes(cardId)) {
        return cardId
      }
    }
    return null
  }
)

/**
 * Selects the headline text for the top open EXPEDITE card
 */
export const selectTopOpenExpediteCardHeadline = createSelector(
  selectTopOpenExpediteCard,
  (topCard) => (topCard ? EXPEDITE_CARD_HEADLINES[topCard] : null)
)

/**
 * Selects whether CUBK is blocked
 * CUBK is blocked when it's incomplete AND any EXPEDITE card is open
 */
export const selectIsCubkBlocked = createSelector(
  selectIsCatchUpBookkeepingComplete,
  selectHasOpenExpediteCards,
  (isCubkComplete, hasOpenCards) => !isCubkComplete && hasOpenCards
)

/**
 * Selects the deadline date based on entity type and VTE completion
 * Uses tax details from the current tax year
 * - Sole Prop → form_1040_eoy_survey_due_date
 * - S-Corp + VTE complete → form_1120_s_eoy_survey_due_date
 * - S-Corp + VTE incomplete → tax_entity_verification_due_date
 */
export const selectCubkDeadlineDate = createSelector(
  selectTskIsScorp,
  selectIsVerifyTaxEntityComplete,
  selectCurrentAnnualTaxDetails,
  (isScorp, isVteComplete, taxDetails) => {
    if (!taxDetails) {
      return null
    }

    if (isScorp) {
      if (isVteComplete) {
        return taxDetails.form_1120_s_eoy_survey_due_date || null
      }
      return taxDetails.tax_entity_verification_due_date || null
    }

    // Sole Prop
    return taxDetails.form_1040_eoy_survey_due_date || null
  }
)

/**
 * Determines whether user has opted into annual taxes for the current tax year
 * User has opted in if they have a filing for the current year and haven't opted out
 */
export const selectHasOptedIntoAnnualTax = createSelector(
  selectCurrentAnnualTaxFiling,
  (taxFiling) => Boolean(taxFiling) && !taxFiling?.optedOutAt
)

/**
 * Main selector: determines whether to show the CUBK deadline banner
 *
 * Show when ALL conditions are true:
 * - Not a late-joiner (for current tax year)
 * - TSK is complete (for current tax year)
 * - Opted into Annual Tax (has a filing for current tax year)
 * - At least one EXPEDITE card is open
 * - CUBK is not complete
 * - A valid deadline date exists
 */
export const selectShouldShowCubkDeadlineBanner = createSelector(
  selectUserIsLateJoinerForCubk,
  selectIsTskComplete,
  selectHasOptedIntoAnnualTax,
  selectHasOpenExpediteCards,
  selectIsCatchUpBookkeepingComplete,
  selectCubkDeadlineDate,
  (
    isLateJoiner,
    isTskComplete,
    hasOptedIn,
    hasOpenCards,
    isCubkComplete,
    deadlineDate
  ) => {
    // Don't show if late joiner
    if (isLateJoiner) {
      return false
    }

    // Don't show if TSK not complete
    if (!isTskComplete) {
      return false
    }

    // Don't show if not opted into annual tax
    if (!hasOptedIn) {
      return false
    }

    // Don't show if CUBK is already complete
    if (isCubkComplete) {
      return false
    }

    // Show only if EXPEDITE cards are open and a valid deadline date exists
    return hasOpenCards && Boolean(deadlineDate)
  }
)
